import { useMemo } from 'react';

import clsx from 'clsx';

import useMedias from '@/hooks/swr/useMedias';
import useStories from '@/hooks/swr/useStories';
import useUser from '@/hooks/swr/useUser';
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import useGroupAnalytics from '@/utils/hooks/swr/group/useGroupAnalytics';
import useAnalytics from '@/utils/hooks/swr/useAnalytics';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import type { AlbumCriteria, AlbumTypes } from '@/lib/albums/types';

import {
    AlbumCardAnalyticsIcons,
    AlbumCardAnalyticsIconsForGroups,
    AlbumCardAnalyticsIconsForGroupStories
} from '@/lib/albums/albumCardIconType';
import { FACEBOOK, INSTAGRAM } from '@/lib/socialProfile';

import type { AlbumAnalyticsType } from '@/components/base/Card';
import Card from '@/components/base/Card';
import AlbumCardMediaGrid from '@/components/pages/albums/AlbumCardMediaGrid';
import AlbumCardPlaceholder from '@/components/pages/albums/AlbumCardPlaceholder';

type AlbumCardType = {
    albumId: number;
    countMedias: number;
    title: string;
    type: AlbumTypes;
    criteria: AlbumCriteria;
    className?: string;
    onClick: () => void;
};

const AlbumCard = ({
    albumId,
    countMedias,
    title,
    type,
    className = '',
    criteria,
    onClick
}: AlbumCardType) => {
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();
    const isStoriesCampaign = criteria?.album_type === 'stories';

    // Here we fetch the campaign medias (posts)
    const { dataMedias, isLoadingInitialData: isLoadingMedias } = useMedias({
        groupID: isGroup && !isStoriesCampaign ? activeGroup?.id : undefined,
        activeSPID: !isStoriesCampaign ? activeSocialProfile?.id : undefined,
        params: !isStoriesCampaign
            ? {
                  album_id: albumId
              }
            : null,
        limit: 4
    });

    // Here we fetch the campaign stories
    const { dataStories, isLoadingInitialData: isLoadingStories } = useStories({
        groupID: isGroup && isStoriesCampaign ? activeGroup?.id : undefined,
        activeSPID: isStoriesCampaign ? activeSocialProfile?.id : undefined,
        params: isStoriesCampaign
            ? {
                  album_id: albumId
              }
            : null,
        limit: 4
    });

    // Use the appropriate data based on campaign type
    const campaignMedias = isStoriesCampaign ? dataStories : dataMedias;
    const isLoadingInitialData = isStoriesCampaign ? isLoadingStories : isLoadingMedias;

    // Determine network type (only relevant for social profiles, not groups)
    const network = activeSocialProfile?.social_network_type;

    // Determine analytics fields to request based on context (group or social profile)
    // Fields are derived from AlbumCardAnalyticsIcons to ensure consistency
    const analyticsFields: string[] = useMemo(() => {
        // For groups: use stories-specific or posts-specific fields
        if (isGroup) {
            return isStoriesCampaign
                ? (Object.keys(AlbumCardAnalyticsIconsForGroupStories) as string[])
                : (Object.keys(AlbumCardAnalyticsIconsForGroups) as string[]);
        }

        // For social profiles: use network-specific fields
        if (!network) return [];

        const isStories = criteria?.album_type === 'stories';
        const availableIcons = AlbumCardAnalyticsIcons[network];

        if (!availableIcons) return [];

        // Get all field keys for the network
        const fields = Object.keys(availableIcons) as string[];

        // For Instagram stories, filter to only show stories-specific fields
        if (network === INSTAGRAM && isStories) {
            return fields.filter((field: string) => field.includes('stories'));
        }

        // For Instagram non-stories, exclude stories-specific fields
        if (network === INSTAGRAM && !isStories) {
            return fields.filter((field: string) => !field.includes('stories'));
        }

        return fields;
    }, [isGroup, isStoriesCampaign, network, criteria?.album_type]);

    // Fetch analytics for Social Profiles (only if not a group)
    const { analytics: spAnalytics, isLoading: isLoadingSPAnalytics } = useAnalytics(
        !isGroup ? activeSocialProfile?.id : null,
        {
            album_id: albumId,
            fields: analyticsFields
        },
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );

    // Fetch analytics for Groups (only if it's a group)
    const { groupAnalytics, isLoading: isLoadingGroupAnalytics } = useGroupAnalytics({
        activeGroupID: isGroup ? activeGroup?.id : undefined,
        params: {
            album_id: albumId,
            fields: analyticsFields as any
        },
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    // Format analytics for Card.Footer
    const formattedAnalytics: AlbumAnalyticsType = useMemo(() => {
        // Extract analytics based on context
        // For groups: groupAnalytics is an array, we need the group data (first item with 'group' key)
        // For social profiles: spAnalytics is a simple object
        let analyticsData: Record<string, any> | null = null;

        if (isGroup && groupAnalytics?.length) {
            analyticsData = groupAnalytics[0];
        } else if (!isGroup && spAnalytics) {
            analyticsData = spAnalytics;
        }

        if (!analyticsData) return {};

        // Use group-specific (posts or stories) or network-specific icons configuration
        const availableIcons = isGroup
            ? isStoriesCampaign
                ? AlbumCardAnalyticsIconsForGroupStories
                : AlbumCardAnalyticsIconsForGroups
            : AlbumCardAnalyticsIcons[network];

        if (!availableIcons) return {};

        const formatted: AlbumAnalyticsType = {};

        // Only include fields that are defined in the icons configuration
        Object.entries(availableIcons).forEach(([fieldName]) => {
            const value = analyticsData?.[fieldName];
            if (value !== undefined && value !== null) {
                formatted[fieldName] = { value: value?.value };
            }
        });

        return formatted;
    }, [isGroup, isStoriesCampaign, groupAnalytics, spAnalytics, network]);

    // Determine if we're loading
    const isLoading =
        isLoadingInitialData || (isGroup ? isLoadingGroupAnalytics : isLoadingSPAnalytics);

    // Show placeholder while loading
    if (isLoading) {
        return <AlbumCardPlaceholder className={className} />;
    }

    return (
        <div
            className={clsx('h-full w-full', className)}
            role="button"
            data-testid="album-card"
            aria-hidden={true}
            onClick={onClick}>
            <Card>
                <Card.Header className="relative">
                    <AlbumCardMediaGrid
                        forcedNetwork={isGroup ? FACEBOOK : undefined}
                        medias={
                            isStoriesCampaign
                                ? campaignMedias?.[0]?.stories || []
                                : campaignMedias?.[0]?.medias || []
                        }
                        isGroup={isGroup}
                        type={type}
                        criteria={criteria}
                    />
                </Card.Header>
                <div className="flex flex-1 flex-col justify-between space-y-m">
                    <Card.Content title={title} countMedias={countMedias} criteria={criteria} />
                    <Card.Footer
                        analytics={formattedAnalytics}
                        criteria={criteria}
                        userNumbersFormat={user?.settings?.numbers_format}
                    />
                </div>
            </Card>
        </div>
    );
};

export default AlbumCard;
